import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen } from "@fortawesome/free-solid-svg-icons"
import {
	fetchInventaireConsommables,
	fetchInventaireConsommations,
} from "../../../store/actions/atelierInventaireActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"
import ConsommableModalV2 from "./ConsommableModalV2"
import ConsommationModalV2 from "./ConsommationModalV2"

const ConsommableRow = ({ consommable, index, selected, onSelect, onEdit }) => {
	const [hover, setHover] = useState(false)
	const bg = selected
		? COLORS.accentLight
		: hover
			? tableStyles.rowHover
			: tableStyles.rowBg(index)
	return (
		<tr
			onClick={() => onSelect(consommable)}
			onDoubleClick={() => onEdit(consommable)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td
				style={{
					...tableStyles.td,
					fontWeight: selected ? 600 : 500,
					color: selected ? COLORS.accent : COLORS.text,
				}}
			>
				{consommable.label || "—"}
			</td>
		</tr>
	)
}

const ConsommationRow = ({ conso, index, onEdit }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={() => onEdit(conso)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={{ ...tableStyles.td, color: COLORS.textMuted }}>
				{conso.date}
			</td>
			<td style={{ ...tableStyles.td, fontWeight: 600 }}>
				{conso.article_label || "—"}
			</td>
			<td style={tableStyles.tdRight}>{conso.quantite}</td>
			<td style={{ ...tableStyles.td, color: COLORS.textMuted }}>
				{conso.employe_nom || "—"}
			</td>
			<td style={s.tdCenter}>
				<button
					type="button"
					title="Modifier / supprimer"
					onClick={(e) => {
						e.stopPropagation()
						onEdit(conso)
					}}
					style={gs.iconBtn(COLORS.textMuted)}
				>
					<FontAwesomeIcon icon={faPen} />
				</button>
			</td>
		</tr>
	)
}

// Sous-onglet "Stock virtuel" de Options : master-détail catégories de
// consommable → consommations (correction/annulation d'erreurs de saisie).
const OptionsStockVirtuelV2 = () => {
	const dispatch = useDispatch()
	const consommables = useSelector((s) => s.atelierInventaire.consommables)
	const fetchConsommables = useSelector(
		(s) => s.atelierInventaire.fetchConsommables,
	)
	const consommations = useSelector((s) => s.atelierInventaire.consommations)
	const fetchConsommations = useSelector(
		(s) => s.atelierInventaire.fetchConsommations,
	)

	const [selectedId, setSelectedId] = useState(null)
	const [editingConsommable, setEditingConsommable] = useState(null)
	const [editingConso, setEditingConso] = useState(null)

	useEffect(() => {
		dispatch(fetchInventaireConsommables())
	}, [dispatch])

	const sorted = [...(consommables || [])].sort((a, b) =>
		(a.label || "").localeCompare(b.label || "", "fr"),
	)
	const selected = sorted.find((c) => c.id === selectedId) || null

	const selectConsommable = (c) => {
		setSelectedId(c.id)
		dispatch(fetchInventaireConsommations(c.id))
	}

	const listLoading = fetchConsommables.loading && sorted.length === 0

	return (
		<>
			{editingConsommable && (
				<ConsommableModalV2
					consommable={editingConsommable}
					onClose={() => setEditingConsommable(null)}
				/>
			)}
			{editingConso && (
				<ConsommationModalV2
					consommation={editingConso}
					consommables={sorted}
					onClose={() => setEditingConso(null)}
				/>
			)}

			<div style={s.wrap}>
				{/* ───── Catégories de consommable ───── */}
				<div style={s.leftCol}>
					<div style={s.panelHead}>
						<span style={s.panelTitle}>Consommables</span>
						<button
							style={gs.btnPrimary}
							onClick={() => setEditingConsommable({})}
						>
							+ Consommable
						</button>
					</div>
					<div style={tableStyles.container}>
						{listLoading ? (
							<Loader size={28} padding={24} />
						) : sorted.length === 0 ? (
							<div style={tableStyles.empty}>Aucun consommable.</div>
						) : (
							<table style={tableStyles.table}>
								<tbody>
									{sorted.map((c, i) => (
										<ConsommableRow
											key={c.id}
											consommable={c}
											index={i}
											selected={c.id === selectedId}
											onSelect={selectConsommable}
											onEdit={setEditingConsommable}
										/>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>

				{/* ───── Consommations du consommable sélectionné ───── */}
				<div style={s.rightCol}>
					{!selected ? (
						<div style={s.placeholder}>
							Sélectionnez un consommable pour voir ses
							consommations.
						</div>
					) : (
						<>
							<div style={s.panelHead}>
								<span style={s.panelTitle}>
									Consommations · {selected.label}
								</span>
							</div>
							<div style={tableStyles.container}>
								{fetchConsommations.loading ? (
									<Loader size={28} padding={24} />
								) : (consommations || []).length === 0 ? (
									<div style={tableStyles.empty}>
										Aucune consommation.
									</div>
								) : (
									<table style={tableStyles.table}>
										<thead>
											<tr>
												<th style={tableStyles.th}>Date</th>
												<th style={tableStyles.th}>
													Article
												</th>
												<th style={tableStyles.thRight}>
													Quantité
												</th>
												<th style={tableStyles.th}>
													Employé
												</th>
												<th style={s.thCenter} />
											</tr>
										</thead>
										<tbody>
											{consommations.map((conso, i) => (
												<ConsommationRow
													key={conso.id}
													conso={conso}
													index={i}
													onEdit={setEditingConso}
												/>
											))}
										</tbody>
									</table>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</>
	)
}

const s = {
	wrap: { display: "flex", gap: 20, alignItems: "flex-start" },
	leftCol: { width: 280, flexShrink: 0 },
	rightCol: { flex: 1, minWidth: 0 },
	panelHead: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 12,
		gap: 10,
		minHeight: 38,
	},
	panelTitle: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	thCenter: { ...tableStyles.th, width: 60 },
	tdCenter: {
		...tableStyles.td,
		padding: "8px 16px",
		textAlign: "center",
	},
	placeholder: {
		padding: "60px 16px",
		textAlign: "center",
		color: COLORS.textLight,
		fontSize: 14,
		backgroundColor: COLORS.surface,
		borderRadius: 14,
		boxShadow:
			"0 1px 2px rgba(28, 25, 23, 0.04), 0 6px 20px rgba(28, 25, 23, 0.06)",
	},
}

export default OptionsStockVirtuelV2
